import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../../contexts/ThemeContext';
import OptionBlock from './OptionBlock';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const darkSubmitReady = {
  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
  color: '#1d4746',
  boxShadow: '0 4px 14px rgba(251, 191, 36, 0.35)',
};
const darkSubmitDisabled = {
  background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
  color: '#64748b',
  boxShadow: 'none',
  opacity: 0.7,
};

const Sequencing = ({ question, onAnswer, onNext, sessionData = null, variant = 'default' }) => {
  const { isDarkMode } = useTheme();
  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    // Backend structure: choices array with idx and text, blocks array, correct-answers
    // PENTING: Urutan tampilan options harus sesuai urutan choices array dari backend
    // Bukan berdasarkan blocks atau correct answers
    
    let items = [];
    let blocksForAnswer = []; // Untuk validasi jawaban (urutan yang benar)
    
    // Jika ada blocks, simpan untuk validasi jawaban
    if (question.blocks && Array.isArray(question.blocks)) {
      const choices = question.choices || [];
      
      blocksForAnswer = question.blocks.map((blockText, index) => {
        // Cari choice yang text-nya match dengan blockText untuk mendapatkan idx
        const matchedChoice = choices.find(choice => {
          const choiceText = choice.text || choice['option-text'] || '';
          return choiceText === blockText || choiceText.trim() === blockText.trim();
        });
        
        // Gunakan idx dari choices untuk validasi
        const choiceIdx = matchedChoice 
          ? (matchedChoice.idx !== undefined ? matchedChoice.idx : matchedChoice.id)
          : null;
        
        return {
          idx: choiceIdx !== null ? choiceIdx : index,
          text: blockText || '',
        };
      });
    }
    
    // URUTAN TAMPILAN OPTIONS: Gunakan urutan dari choices array (bukan blocks)
    // JANGAN SORT - gunakan urutan asli dari backend choices array
    if (question.choices && Array.isArray(question.choices)) {
      // Urutan choices dari backend harus dipertahankan (bukan diurutkan berdasarkan idx)
      // Contoh: backend kirim [idx:1 "heißen", idx:3 "bitte", idx:2 "Sie", idx:0 "Wie"]
      // → tampilkan: "heißen", "bitte", "Sie", "Wie" (sesuai urutan array choices)
      items = question.choices.map((choice) => ({
        id: choice.idx !== undefined ? choice.idx : choice.id,
        idx: choice.idx !== undefined ? choice.idx : choice.id,
        text: choice.text || '',
      }));
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔍 Sequencing - Choices order (no sort):', {
        //   originalChoices: question.choices.map(c => ({ idx: c.idx, text: c.text })),
        //   items: items.map(item => ({ idx: item.idx, text: item.text })),
        //   blocksForAnswer: blocksForAnswer.map(b => ({ idx: b.idx, text: b.text })),
        // });
      }
    }

    return {
      ...question,
      items: items, // Urutan sesuai choices array dari backend
      blocksForAnswer: blocksForAnswer, // Untuk validasi jawaban (urutan yang benar)
      text: question.text || question['text-soal'] || '',
    };
  }, [question]);

  const [availableItems, setAvailableItems] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const [draggedOption, setDraggedOption] = useState(null);
  const [dragOverBlank, setDragOverBlank] = useState(false);
  const [dragFromAnswerIndex, setDragFromAnswerIndex] = useState(null);
  const [dragOverAnswerIndex, setDragOverAnswerIndex] = useState(null);
  const activeAnnotationRef = useRef(null);
  const annotationAudioRef = useRef(null);

  // Reset state when question changes
  // JANGAN SHUFFLE - gunakan urutan asli dari response
  useEffect(() => {
    if (annotationAudioRef.current) {
      annotationAudioRef.current.pause();
      annotationAudioRef.current = null;
    }
    if (transformedQuestion.items && transformedQuestion.items.length > 0) {
      // Urutan asli dari response, tidak di-acak, tidak di-sort
      setAvailableItems([...transformedQuestion.items]);
      setUserAnswers([]);
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔍 Sequencing - Available items order (no shuffle, no sort):', {
        //   items: transformedQuestion.items.map(item => ({ idx: item.idx, text: item.text })),
        //   availableItems: [...transformedQuestion.items].map(item => ({ idx: item.idx, text: item.text })),
        // });
      }
    }
  }, [question['soal-id'] || question.id, transformedQuestion.items]);

  // CRITICAL: Gunakan useMemo untuk memastikan filtering selalu menggunakan state terbaru
  // dan re-render ketika userAnswers berubah
  const availableOptions = useMemo(() => {
    // Buat set dari idx yang sudah digunakan di userAnswers
    // Setiap idx yang sudah digunakan tidak boleh muncul lagi di available options
    // CRITICAL: Normalize semua ke Number untuk perbandingan yang konsisten
    const usedIdxSet = new Set();
    userAnswers.forEach(answerIdx => {
      if (answerIdx !== undefined && answerIdx !== null) {
        // Normalize ke number untuk konsistensi perbandingan
        const normalizedIdx = Number(answerIdx);
        if (!isNaN(normalizedIdx)) {
          usedIdxSet.add(normalizedIdx);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('🔍 Sequencing - Invalid answerIdx in userAnswers:', answerIdx);
          }
        }
      }
    });
    
    // Filter items: hanya tampilkan items yang idx-nya belum digunakan
    // PENTING: Pertahankan urutan dari availableItems (urutan asli dari backend)
    const filteredItems = availableItems.filter(item => {
      // Normalize idx untuk perbandingan yang konsisten
      const itemIdx = Number(item.idx);
      if (isNaN(itemIdx)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - Invalid item idx:', item);
        }
        return true; // Include invalid items (shouldn't happen)
      }
      
      // Cek apakah idx sudah digunakan (perbandingan dengan normalized number)
      const isUsed = usedIdxSet.has(itemIdx);
      
      return !isUsed;
    });
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 Sequencing - getAvailableOptions (useMemo):', {
      //   availableItemsCount: availableItems.length,
      //   filteredItemsCount: filteredItems.length,
      //   userAnswersCount: userAnswers.length,
      //   userAnswers: userAnswers,
      //   userAnswersTypes: userAnswers.map(a => typeof a),
      //   usedIdxSet: Array.from(usedIdxSet),
      //   usedIdxSetTypes: Array.from(usedIdxSet).map(i => typeof i),
      //   availableItemsOrder: availableItems.map(item => ({ 
      //     idx: item.idx, 
      //     idxType: typeof item.idx,
      //     normalizedIdx: Number(item.idx),
      //     text: item.text 
      //   })),
      //   filteredItemsOrder: filteredItems.map(item => ({ 
      //     idx: item.idx, 
      //     text: item.text 
      //   })),
      // });
    }
    
    return filteredItems;
  }, [availableItems, userAnswers]);

  const handleDragStart = (e, option) => {
    setDraggedOption(option);
    setDragFromAnswerIndex(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Drag from answer block (reorder within sentence)
  const handleAnswerBlockDragStart = (e, fromIndex) => {
    if (e.target.closest('.fib-remove-button')) {
      e.preventDefault();
      return;
    }
    setDragFromAnswerIndex(fromIndex);
    setDraggedOption({ type: 'reorder', fromIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `reorder-${fromIndex}`);
  };

  const handleAnswerBlockDragOver = (e, overIndex) => {
    if (dragFromAnswerIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation();
    setDragOverAnswerIndex(overIndex);
  };

  const handleAnswerBlockDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverAnswerIndex(null);
    }
  };

  const handleAnswerBlockDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragFromAnswerIndex === null || dragFromAnswerIndex === dropIndex) {
      setDragFromAnswerIndex(null);
      setDragOverAnswerIndex(null);
      return;
    }
    setUserAnswers(prev => {
      const newAnswers = [...prev];
      const [removed] = newAnswers.splice(dragFromAnswerIndex, 1);
      const insertIndex = dragFromAnswerIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newAnswers.splice(insertIndex, 0, removed);
      return newAnswers;
    });
    setDragFromAnswerIndex(null);
    setDragOverAnswerIndex(null);
  };

  const handleAnswerBlockDragEnd = () => {
    setDragFromAnswerIndex(null);
    setDragOverAnswerIndex(null);
    setDraggedOption(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (dragFromAnswerIndex !== null) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverBlank(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverBlank(false);
    }
  };

  const handleDrop = (e) => {
    if (!draggedOption) return;
    e.preventDefault();
    if (typeof draggedOption === 'object' && draggedOption?.type === 'reorder') {
      setDragFromAnswerIndex(null);
      setDragOverAnswerIndex(null);
      setDraggedOption(null);
      return;
    }
    
    // draggedOption is a string (text) from OptionBlock
    const optionText = typeof draggedOption === 'string' ? draggedOption : draggedOption.text || draggedOption;
    
    // CRITICAL: Gunakan functional update untuk memastikan kita menggunakan state terbaru
    setUserAnswers(prevUserAnswers => {
      // Buat set dari idx yang sudah digunakan
      const usedIdxSet = new Set();
      prevUserAnswers.forEach(answerIdx => {
        if (answerIdx !== undefined && answerIdx !== null) {
          usedIdxSet.add(Number(answerIdx));
        }
      });
      
      // Filter availableItems untuk mendapatkan items yang masih tersedia
      const currentAvailableOptions = availableItems.filter(item => {
        const itemIdx = Number(item.idx);
        return !usedIdxSet.has(itemIdx);
      });
      
      // Cari item dari currentAvailableOptions (yang sudah difilter)
      const item = currentAvailableOptions.find(item => item.text === optionText);
      
      if (!item || item.idx === undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleDrop: item not found or already used', { 
            draggedOption, 
            optionText, 
            currentAvailableOptions: currentAvailableOptions.map(i => ({ idx: i.idx, text: i.text })),
            usedIdxSet: Array.from(usedIdxSet),
          });
        }
        return prevUserAnswers;
      }
      
      // Normalize idx
      const normalizedIdx = Number(item.idx);
      if (isNaN(normalizedIdx)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('🔍 Sequencing - handleDrop: Invalid idx', { item });
        }
        return prevUserAnswers;
      }
      
      // Double check: pastikan belum ada di userAnswers
      if (usedIdxSet.has(normalizedIdx)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleDrop: item already added', { normalizedIdx, prevUserAnswers });
        }
        return prevUserAnswers;
      }
      
      // Check if we've reached max answers
      const expectedLength = transformedQuestion.blocksForAnswer && transformedQuestion.blocksForAnswer.length > 0
        ? transformedQuestion.blocksForAnswer.length
        : transformedQuestion.items.length;
      
      if (prevUserAnswers.length >= expectedLength) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleDrop: max answers reached', { 
            userAnswersLength: prevUserAnswers.length, 
            expectedLength 
          });
        }
        return prevUserAnswers;
      }
      
      // Tambahkan normalized idx ke userAnswers
      const newUserAnswers = [...prevUserAnswers, normalizedIdx];
      
      return newUserAnswers;
    });
    
    setDraggedOption(null);
    setDragOverBlank(false);
  };

  const handleOptionClick = (option, event) => {
    // Prevent event propagation to avoid conflicts with annotation clicks
    if (event) {
      event.stopPropagation();
    }
    
    // option is a string (text) from OptionBlock
    const optionText = typeof option === 'string' ? option : option.text || option;
    
    // CRITICAL: Gunakan functional update untuk memastikan kita menggunakan state terbaru
    // dan mencari dari availableOptions yang sudah difilter
    setUserAnswers(prevUserAnswers => {
      // Buat set dari idx yang sudah digunakan
      const usedIdxSet = new Set();
      prevUserAnswers.forEach(answerIdx => {
        if (answerIdx !== undefined && answerIdx !== null) {
          usedIdxSet.add(Number(answerIdx));
        }
      });
      
      // Filter availableItems untuk mendapatkan items yang masih tersedia
      const currentAvailableOptions = availableItems.filter(item => {
        const itemIdx = Number(item.idx);
        return !usedIdxSet.has(itemIdx);
      });
      
      // Cari item dari currentAvailableOptions (yang sudah difilter)
      const item = currentAvailableOptions.find(item => item.text === optionText);
      
      if (!item || item.idx === undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleOptionClick: item not found or already used', { 
            option, 
            optionText, 
            currentAvailableOptions: currentAvailableOptions.map(i => ({ idx: i.idx, text: i.text })),
            usedIdxSet: Array.from(usedIdxSet),
          });
        }
        return prevUserAnswers; // Return previous state, tidak ada perubahan
      }
      
      // Normalize idx
      const normalizedIdx = Number(item.idx);
      if (isNaN(normalizedIdx)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('🔍 Sequencing - handleOptionClick: Invalid idx', { item });
        }
        return prevUserAnswers;
      }
      
      // Double check: pastikan belum ada di userAnswers
      if (usedIdxSet.has(normalizedIdx)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleOptionClick: item already added', { normalizedIdx, prevUserAnswers });
        }
        return prevUserAnswers;
      }
      
      // Check if we've reached max answers
      const expectedLength = transformedQuestion.blocksForAnswer && transformedQuestion.blocksForAnswer.length > 0
        ? transformedQuestion.blocksForAnswer.length
        : transformedQuestion.items.length;
      
      if (prevUserAnswers.length >= expectedLength) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Sequencing - handleOptionClick: max answers reached', { 
            userAnswersLength: prevUserAnswers.length, 
            expectedLength 
          });
        }
        return prevUserAnswers;
      }
      
      // Tambahkan normalized idx ke userAnswers
      const newUserAnswers = [...prevUserAnswers, normalizedIdx];
      
      return newUserAnswers;
    });
  };

  const handleRemoveAnswer = (index) => {
    setUserAnswers(prev => prev.filter((_, i) => i !== index));
  };

  const checkAllBlanksFilled = () => {
    // Gunakan blocksForAnswer length jika ada (jumlah yang harus diisi)
    // Jika tidak ada blocks, gunakan items length
    const expectedLength = transformedQuestion.blocksForAnswer && transformedQuestion.blocksForAnswer.length > 0
      ? transformedQuestion.blocksForAnswer.length
      : transformedQuestion.items.length;
    return userAnswers.length === expectedLength;
  };

  const handleSubmit = () => {
    if (!checkAllBlanksFilled()) return;
    
    // Validate against correct answers from backend
    // Gunakan blocksForAnswer jika ada (urutan yang benar dari blocks)
    // Jika tidak ada blocks, gunakan correct-answers atau items order
    const correctAnswers = question['correct-answers'] || question['neo-answers'] || question.answers || [];
    let correct = false;
    
    // Priority: gunakan blocksForAnswer jika ada (urutan yang benar)
    if (transformedQuestion.blocksForAnswer && transformedQuestion.blocksForAnswer.length > 0) {
      const correctOrder = transformedQuestion.blocksForAnswer.map(block => block.idx);
      correct = correctOrder.length === userAnswers.length && correctOrder.every((correctIdx, index) => {
        return Number(correctIdx) === Number(userAnswers[index]);
      });
    } else if (Array.isArray(correctAnswers) && correctAnswers[0] && Array.isArray(correctAnswers[0])) {
      // Compare user answers (array of idx) with correct order (array of indices)
      const correctOrder = correctAnswers[0];
      correct = correctOrder.length === userAnswers.length && correctOrder.every((correctIdx, index) => {
        return Number(correctIdx) === Number(userAnswers[index]);
      });
    } else {
      // Fallback: compare with items order (by idx) - ini seharusnya tidak terjadi jika ada blocks
      const correctOrder = transformedQuestion.items.map(item => item.idx);
      correct = correctOrder.length === userAnswers.length && correctOrder.every((correctIdx, index) => {
        return Number(correctIdx) === Number(userAnswers[index]);
      });
    }
    
    if (onAnswer) {
      // Send array of idx from choices, not index array
      // userAnswers berisi idx dari choices (bukan index array)
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔍 Sequencing - Submitting answer:', {
        //   userAnswers,
        //   userAnswersType: userAnswers.map(id => typeof id),
        //   items: transformedQuestion.items.map(item => ({ idx: item.idx, text: item.text })),
        //   correctAnswers,
        //   correct,
        // });
      }
      
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: userAnswers, // Array of idx dari choices: [3, 0] for example (bukan index array)
        isCorrect: correct
      });
    }
    
    if (onNext) {
      onNext();
    }
  };

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const html = transformedQuestion.text || '';
    if (html && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Sequencing: failed to parse annotations from HTML', error);
        }
      }
    }

    return map;
  }, [question.annotations, transformedQuestion.text]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  const firstAnnotationAudioUrl = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
    try {
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');
      for (const node of nodes) {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]?.streamUrl) {
          return annotationsMap[annId].streamUrl;
        }
      }
    } catch {}
    return null;
  }, [transformedQuestion.text, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl;

  // Enhance HTML with annotation classes; hide annotation shown via waveform player
  const enhancedHtml = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Sequencing: failed to enhance HTML', error);
      }
      return html;
    }
  }, [transformedQuestion.text, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;

    if (!annotationAudioRef.current) {
      annotationAudioRef.current = new Audio();
      annotationAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }
    const audioEl = annotationAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (event.target.closest('[data-fib-option]')) return;

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;
  // availableOptions sudah di-memoize di atas dengan useMemo, tidak perlu dipanggil lagi

  // Extract translation from question
  const translation = question.translation || question['translation'] || '';

  const badgeGradient = variant === 'drill'
    ? 'from-[#345d8a] to-[#1e3d6f]'
    : 'from-[#12766d] to-[#236463]';

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Sequencing
        </motion.span>
      </div>
      
      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer audioUrl={displayAudioUrl} />
        )}
        <div 
          className="text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
          onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
          onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
          dangerouslySetInnerHTML={{ __html: enhancedHtml }}
        />
        
        {translation && (
          <>
            <div className="h-px bg-[var(--border-color)] my-4" />
            <div>
              <div className="text-sm font-semibold text-[var(--text-primary)] mb-2">Translation:</div>
              <p className="m-0 text-base text-[var(--text-secondary)] italic">{translation}</p>
            </div>
          </>
        )}
        
        <div 
          className={`sequencing-sentence-container flex flex-wrap items-center gap-1 p-3 text-lg font-semibold text-[var(--text-primary)] leading-relaxed mb-1 rounded-xl min-h-[80px] w-full transition-all duration-300 overflow-y-auto border-2 border-dashed bg-[rgba(18,118,109,0.05)] border-[rgba(18,118,109,0.3)] dark:bg-[rgba(20,184,166,0.06)] dark:border-[rgba(20,184,166,0.3)] max-md:p-2.5 max-md:text-base max-md:min-h-[100px] max-md:h-auto ${
            dragOverBlank ? 'drag-over !border-[#12766d] !bg-[rgba(18,118,109,0.15)] border-solid dark:!border-[#14b8a6] dark:!bg-[rgba(20,184,166,0.15)]' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {userAnswers.length > 0 ? (
            userAnswers.map((answerIdx, index) => {
              const item = transformedQuestion.items.find(item => item.idx === answerIdx);
              const answerText = item ? item.text : `[${answerIdx}]`;
              const isDragging = dragFromAnswerIndex === index;
              const isDragOver = dragOverAnswerIndex === index;
              return (
              <React.Fragment key={index}>
                <div
                  className={`flex items-center gap-1.5 py-1 px-2.5 rounded-md text-sm font-semibold text-white bg-gradient-to-br from-[#12766d] to-[#236463] cursor-grab select-none transition-all duration-200 active:cursor-grabbing max-md:py-0.5 max-md:px-2 max-md:text-[13px] ${
                    isDragging ? 'opacity-50 scale-[0.98]' : ''
                  } ${isDragOver ? 'shadow-[0_0_0_2px_rgba(18,118,109,0.6)] dark:shadow-[0_0_0_2px_rgba(20,184,166,0.6)] scale-105' : ''}`}
                  draggable
                  onDragStart={(e) => handleAnswerBlockDragStart(e, index)}
                  onDragOver={(e) => handleAnswerBlockDragOver(e, index)}
                  onDragLeave={handleAnswerBlockDragLeave}
                  onDrop={(e) => handleAnswerBlockDrop(e, index)}
                  onDragEnd={handleAnswerBlockDragEnd}
                >
                  <span>{answerText}</span>
                  <button
                    data-sequencing-remove
                    type="button"
                    tabIndex={-1}
                    className="inline-flex items-center justify-center border-none bg-transparent text-white cursor-pointer text-lg font-bold leading-none p-0 m-0 ml-2 w-5 h-5 shrink-0 transition-all duration-200 hover:opacity-70 hover:scale-110 max-md:w-[18px] max-md:h-[18px] max-md:text-base max-md:ml-1.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAnswer(index);
                    }}
                  >
                    ×
                  </button>
                </div>
                {index < userAnswers.length - 1 && <span className="mx-0.5"> </span>}
              </React.Fragment>
              );
            })
          ) : (
            <span className="text-[rgba(26,26,46,0.5)] dark:text-[var(--text-tertiary)] text-sm font-medium max-md:text-[13px]">Drop answer here</span>
          )}
        </div>

        {availableOptions.length > 0 && (
          <div className="mt-1 p-3 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.06)] rounded-xl max-md:p-2.5 max-md:mt-2">
            {transformedQuestion.optionsTitle && (
              <h4 className="text-base font-semibold text-[var(--text-primary)] mb-4 m-0 max-md:text-sm max-md:mb-3">
                {transformedQuestion.optionsTitle}
              </h4>
            )}
            <div className="flex flex-wrap gap-2 max-md:gap-1.5">
              {availableOptions.map((item, index) => {
                const uniqueKey = `option-${item.idx}-${item.text}-${index}`;
                return (
                  <OptionBlock
                    key={uniqueKey}
                    option={item.text}
                    onDragStart={handleDragStart}
                    onClick={handleOptionClick}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:pt-3 max-md:pb-20">
        <motion.button
          className="px-8 py-3 rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[var(--text-primary)] shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:bg-gradient-to-br hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-[0_2px_4px_rgba(252,179,25,0.2)] max-md:w-full max-md:py-3.5 max-md:min-h-[44px]"
          onClick={handleSubmit}
          disabled={!checkAllBlanksFilled()}
          style={isDarkMode ? (!checkAllBlanksFilled() ? darkSubmitDisabled : darkSubmitReady) : {}}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Sequencing;

